/* eslint-disable no-nested-ternary, camelcase */
import React, { Component, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

import Tooltip from '../../common/Tooltip';
import ActionButton from '../../common/ActionButton';
import RefreshButton from '../../common/refreshbutton';
import SearchBar from '../common/SearchBar';
import CollapsibleTable from '../bookings/CollapsibleTable';
import { InterviewDropDown, PublishedDropDown } from '../common/dropdowns';
import { SelectRole } from '../../common/SelectRole';
import SendSMSContainer from './send_sms';
import SendNotificationContainer from './SendNotification';
import { withPushFiltersToUrl } from '../common/decorators';
import { BulkActions } from './BulkActions';
import history from '../../../history';
import LoadingSpinner from '../../common/LoadingSpinner';
import { MASS_BOOKING_MODE, SERIES_BOOKING_MODE } from '../../../consts';
import { ErrorMessage } from '../../common';
import {
    TJobItem,
    TFreelancerData,
    TUser,
    TUploadingFreelancers,
    TFreelancerRole,
    TManagingAgency,
    TUploadedDocument,
    TSeries,
    TPageNotification,
} from '../../../types';
import { getErrorMessages } from '../../../utils';
import { TmassBookingPayload } from '../../../actions/MassBooking';
import BooleanDropdown from '../common/BooleanDropdown';
import { AddToAvailabilityListModal } from '../../freelancer-details/AddToAvailabilityList';
import { ModalConfirmationWithErrorAndLoader } from '../../common/ModalConfirmationWithErrorAndLoader';
import { executeHttpRequest } from '../../../epics/api';
import { usePrevious } from '../../../hooks';

let axiosAbortController: AbortController;

const massBookingResponseStatuses = {
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
};

type TTableRow = {
    original: {
        id: number;
        is_active: boolean;
        published: boolean;
        photo_thumbnail_large: string;
        unpublication_reason: boolean;
        booking: any;
        has_upcoming_booking: boolean;
    };
    value: any;
};

type TMassBookingConfirmationModal = {
    handleCancel: () => void;
    handleContinue: () => void;
    isBookingFreelancersMode: boolean;
    isSeriesBookingFreelancersMode: boolean;
    isInviteFreelancersMode: boolean;
    isMassBookingCreating: boolean;
    massBookingResultsError: any;
    massBookingTitle: string;
    selectedFreelancers: TFreelancerData[];
    selectedJobs: TJobItem[];
    selectedSeries: TSeries;
    notification: TPageNotification;
};

function MassBookingConfirmationModal({
    handleCancel,
    handleContinue,
    isBookingFreelancersMode,
    isSeriesBookingFreelancersMode,
    isInviteFreelancersMode,
    isMassBookingCreating,
    massBookingResultsError,
    massBookingTitle,
    selectedFreelancers,
    selectedJobs,
    selectedSeries,
    notification,
}: TMassBookingConfirmationModal) {
    const isOpen = isBookingFreelancersMode
        || isSeriesBookingFreelancersMode
        || isInviteFreelancersMode;

    // Mass booking confirmation modal
    const listOfIds = (id: number, index: number) =>
        <span key={id}>
            {!!index && ', '}
            <strong>{id}</strong>
        </span>;

    const actionType = isInviteFreelancersMode ? 'invite' : 'book';
    const freelancerIds = selectedFreelancers.map(({ id }, index) => listOfIds(id, index));
    const jobType = isSeriesBookingFreelancersMode ? 'ongoing job' : 'jobs';
    const jobIds = isSeriesBookingFreelancersMode
        ? listOfIds(selectedSeries.id, 0)
        : selectedJobs.map(({ id }, index) => listOfIds(id, index));

    // Umbrella engagements
    const [isCheckingEngagements, setIsCheckingEngagements] = useState(false);
    const [requiredEngagements, setRequiredEngagements] = useState([]);

    const checkUmbrellaEngagements = () => {
        const umbrellaIds = [];
        if (isSeriesBookingFreelancersMode) {
            if (selectedSeries.umbrella_details) {
                umbrellaIds.push(selectedSeries.umbrella_details.id);
            }
        } else {
            selectedJobs.forEach(job => {
                if (job.umbrella_details) {
                    umbrellaIds.push(job.umbrella_details.id);
                }
            });
        }
        const uniqueIds = [...new Set(umbrellaIds)];
        if (uniqueIds.length) {
            setIsCheckingEngagements(true);
            uniqueIds.forEach((umbrellaId, umbrellaIndex) =>
                selectedFreelancers.forEach((freelancer, freelancerIndex) =>
                    executeHttpRequest(`umbrellas/${umbrellaId}/freelancers/${freelancer.id}/`)
                        .then(response => {
                            if (response?.data?.content?.engaged === false) {
                                setRequiredEngagements(prev => [
                                    ...prev,
                                    { umbrella: umbrellaId, freelancer: freelancer.id, status: null }
                                ]);
                            }
                        })
                        .catch(err => {
                            notification.error('Could not check umbrella engagement', getErrorMessages(err));
                        })
                        .finally(() => {
                            if (
                                umbrellaIndex === uniqueIds.length - 1 &&
                                freelancerIndex === selectedFreelancers.length - 1
                            ) {
                                setIsCheckingEngagements(false);
                            }
                        })
                )
            );
        }
    };

    useEffect(() => {
        if (isOpen && (isBookingFreelancersMode || isSeriesBookingFreelancersMode)) {
            checkUmbrellaEngagements();
        }
    }, [isOpen]);

    const createUmbrellaEngagements = async() => {
        const promises = requiredEngagements.map(async(engagement, index) => {
            const payload: any = { freelancer: engagement.freelancer };
            if (isSeriesBookingFreelancersMode) {
                payload.series = selectedSeries.id;
            } else {
                payload.jobrequest = selectedJobs[0].id;
            }
            try {
                await executeHttpRequest('umbrellas/engagements/', 'post', payload);
                return setRequiredEngagements(prev => prev.filter(eng => !isEqual(eng, engagement)));
            } catch (err) {
                setRequiredEngagements(prev_1 => {
                    const updatedEngagements = [...prev_1];
                    updatedEngagements[index].error = getErrorMessages(err);
                    return updatedEngagements;
                });
                throw new Error;
            }
        });
        return Promise.all(promises);
    };

    const handleConfirm = () => {
        if (requiredEngagements.length) {
            setIsCheckingEngagements(true);
            createUmbrellaEngagements()
                .then(handleContinue)
                .finally(() => setIsCheckingEngagements(false));
        } else {
            handleContinue();
        }
    };

    const handleCancelModal = () => {
        setRequiredEngagements([]);
        handleCancel();
    };

    const renderRequiredEngagements = () => (
        <div className="confirmation-jobs-mass-booking">
            <p className="margin-bottom-20">
                A referral to umbrella companies is required before the following workers can be booked into
                selected {jobType}. By pressing CONTINUE their data will be transferred to appropriate umbrella
                companies and mark them as being engaged by them.
            </p>
            {requiredEngagements.map(engagement => {
                const freelancer = selectedFreelancers.find(fr =>
                    fr.id === engagement.freelancer
                );
                const umbrella = isSeriesBookingFreelancersMode
                    ? selectedSeries.umbrella_details
                    : selectedJobs.find(job =>
                        job.umbrella_details?.id === engagement.umbrella).umbrella_details;

                const freelancerName = `${freelancer?.first_name} ${freelancer?.last_name}`;
                const umbrellaName = umbrella?.legal_name;
                const errorMessage = engagement.error
                    ? (
                        <span className="error-icon">
                            &ndash; ERROR{' '}
                            <Tooltip tooltip={engagement.error}>
                                <i className="icon icon-error" />
                            </Tooltip>
                        </span>
                    ) : '';
                return (
                    <p key={`${engagement.umbrella}-${engagement.freelancer}`}>
                        {freelancerName} &ndash;&gt; {umbrellaName}{errorMessage}
                    </p>
                );
            })}
        </div>
    );

    // Add DVLA check modal
    const [isModalAddCheckOpen, setIsModalAddCheckOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [freelancerWithInvalidCheck, setFreelancerWithInvalidCheck] = useState(null);
    const [modalContent, setModalContent] = useState(null);
    const prevMassBookingResultsError = usePrevious(massBookingResultsError);

    const handleOpenAddCheckModal = () => {
        if (!massBookingResultsError) {
            return;
        }
        const prevError = JSON.stringify(prevMassBookingResultsError?.response?.data?.meta);
        const currentError = JSON.stringify(massBookingResultsError?.response?.data?.meta);
        if (prevError === currentError) {
            return;
        }
        const isDvlaCheckSessionError =
            massBookingResultsError.response.data.meta.detail.hasOwnProperty('id_to_add_check');
        if (!isDvlaCheckSessionError) {
            return;
        }
        const freelancerId = Number(massBookingResultsError.response.data.meta.detail.id_to_add_check[0]);
        const freelancer = selectedFreelancers.find(fr => fr.id === freelancerId);
        setFreelancerWithInvalidCheck(freelancer);
        setModalContent(getErrorMessages(massBookingResultsError));
        setIsModalAddCheckOpen(true);
    };

    const handleCancelAddCheck = () => {
        setError(null);
        setFreelancerWithInvalidCheck(null);
        setIsModalAddCheckOpen(false);
        handleCancelModal();
    };

    useEffect(() => {
        handleOpenAddCheckModal();
    }, [massBookingResultsError]);

    const handleAddCheck = () => {
        setIsLoading(true);
        setError(null);

        const refNumber = isSeriesBookingFreelancersMode
            ? selectedSeries.series_reference_number
            : selectedJobs[0].reference_number;

        executeHttpRequest(
            `freelancers/${freelancerWithInvalidCheck.id}/dvla-check-sessions/`,
            'post',
            { reference_number: refNumber },
        )
            .then(() => {
                setIsLoading(false);
                handleCancelAddCheck();
                notification.success(
                    'Success',
                    'A new check has been submitted. Please approve this check from the Documents Review screen.'
                );
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    return (
        <Modal
            className="modal"
            contentLabel={massBookingTitle}
            onRequestClose={handleCancelModal}
            ariaHideApp={false}
            isOpen={isOpen}
        >
            <p className="modal-title">{massBookingTitle}</p>
            <div className="confirmation-jobs-mass-booking">
                Are you sure you want to {actionType} workers: {freelancerIds} to selected {jobType}: {jobIds}?
            </div>
            {!!requiredEngagements.length && renderRequiredEngagements()}
            {massBookingResultsError && !isMassBookingCreating && !isModalAddCheckOpen && (
                <ErrorMessage message={getErrorMessages(massBookingResultsError)} />
            )}
            <button
                className="continue btn--round-big"
                onClick={handleConfirm}
                disabled={isMassBookingCreating || isCheckingEngagements}
                type="button"
            >
                {isMassBookingCreating || isCheckingEngagements ? (
                    <LoadingSpinner
                        className="mass-booking-spinner"
                        backgroundColor="white"
                    />
                ) : (
                    'CONTINUE'
                )}
            </button>
            {!isMassBookingCreating && !isCheckingEngagements && !isModalAddCheckOpen && (
                <button className="back" onClick={handleCancelModal} type="button">
                    CANCEL
                </button>
            )}
            {isModalAddCheckOpen &&
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={error}
                    handleCancel={handleCancelAddCheck}
                    handleContinue={handleAddCheck}
                    isError={!!error}
                    isInProgress={isLoading}
                    setIsInProgress={setIsLoading}
                    isOpen={isModalAddCheckOpen}
                    title="Add DVLA Check"
                >
                    <p className="modal-title">DVLA Check</p>
                    <p>{modalContent}</p>
                    <p>Would you like a new check to be made?</p>
                </ModalConfirmationWithErrorAndLoader>
            }
        </Modal>
    );
}

type TMassBookingResult = {
    status: string;
    freelancer: {
        id: string;
        dvla_check_expires_soon: boolean;
    };
    jobrequest: {
        id: string;
    };
    message: string;
}

type TFreelancersTableProps = {
    data: any[];
    pages: number;
    showPaginationBottom: boolean;
    loading: boolean;
    notification: TPageNotification;
    user: TUser;
    location: any;
    history: any;
    uploadingFreelancers: TUploadingFreelancers;
    selectedFreelancers: any[];
    selectedJobs: any[];
    selectedSeries: TSeries;
    massBookingResults: TMassBookingResult[];
    massBookingResultsError: any;
    currentPage: number;
    reloadData: () => void;
    setSelectedFreelancers: (allSelectedFreelancers: TFreelancerData[]) => void;
    isMassBookingCreating: boolean;
    createMassBooking: () => void;
    createMassBookingAsync: (
        massBookingPayloadParam: TmassBookingPayload,
        isInviteFreelancersModeParam: boolean,
        axiosAbortControllerParam: AbortController,
    ) => void;
    match: any;
    meta: any;
    onFilterChange: () => void;
    onPageChange: () => void;
    onSortedChange: () => void;
};

type TFreelancersTableState = {
    isBulkMode: boolean;
    isBulkExclusiveMode: boolean;
    showSMS: boolean;
    showNotification: boolean;
    showAddToList: boolean;
    isBookingFreelancersMode: boolean;
    isSeriesBookingFreelancersMode: boolean;
    isInviteFreelancersMode: boolean;
    lastCheckedRowIndex: number;
    lastCheckedRowArrayIndex: number;
};

// @ts-ignore
@withPushFiltersToUrl
export default class FreelancersTable extends Component<
    TFreelancersTableProps,
    TFreelancersTableState
> {
    state: TFreelancersTableState = {
        isBulkMode: false,
        isBulkExclusiveMode: false,
        showSMS: false,
        showNotification: false,
        showAddToList: false,
        isBookingFreelancersMode: false,
        isSeriesBookingFreelancersMode: false,
        isInviteFreelancersMode: false,
        lastCheckedRowIndex: null,
        lastCheckedRowArrayIndex: null,
    };

    openCreateFreelancers = () => {
        history.push('/freelancers/create');
    };

    componentDidUpdate(prevProps: TFreelancersTableProps) {
        if (
            !prevProps.uploadingFreelancers.imported &&
            this.props.uploadingFreelancers.imported
        ) {
            if (
                this.props.uploadingFreelancers.payload.created ||
                this.props.uploadingFreelancers.payload.attached
            ) {
                this.props.reloadData();
            }
        }
    }

    componentDidMount() {
        axiosAbortController = new AbortController();
    }

    componentWillUnmount() {
        axiosAbortController.abort();
    }

    onRowCheckboxChange = (rowInfo: TTableRow, checked: boolean, shiftKeyPressed: boolean) => {
        const { lastCheckedRowIndex, lastCheckedRowArrayIndex, isBulkExclusiveMode } = this.state;

        const rowIndex = rowInfo.original.id;
        const rowArrayIndex = this.props.data.findIndex(
            (object: { id: number; }) => object.id === rowIndex
        );
        let allSelectedFreelancers;

        if (shiftKeyPressed && this.props.selectedFreelancers.length > 0) {
            // handle selection with 'shift' key
            const start = Math.min(lastCheckedRowArrayIndex, rowArrayIndex);
            const end = Math.max(lastCheckedRowArrayIndex, rowArrayIndex);

            const rowSelectedFreelancers = this.props.data.filter(
                (_: never, index: number) => index >= start && index <= end
            );

            if (this.props.selectedFreelancers.some(
                freelancer => freelancer.id === lastCheckedRowIndex)
            ) {
                allSelectedFreelancers = new Set([
                    ...this.props.selectedFreelancers,
                    ...rowSelectedFreelancers
                ]);
            } else {
                allSelectedFreelancers = this.props.selectedFreelancers.filter(
                    freelancer => !rowSelectedFreelancers.some(
                        selected => selected.id === freelancer.id
                    )
                );
            }

            allSelectedFreelancers = Array.from(allSelectedFreelancers);

        } else if ((!isBulkExclusiveMode && checked) || (isBulkExclusiveMode && !checked)) {
                allSelectedFreelancers = this.props.selectedFreelancers.concat(
                    rowInfo.original,
                );
            } else {
                allSelectedFreelancers = this.props.selectedFreelancers.filter(
                    item => item.id !== rowInfo.original.id,
                );
            }

        this.setState({
            lastCheckedRowIndex: rowIndex,
            lastCheckedRowArrayIndex: rowArrayIndex,
        });
        this.props.setSelectedFreelancers(allSelectedFreelancers);
    };

    onToggleBulkMode = () => {
        this.setState(prevState => ({
            isBulkMode: !prevState.isBulkMode,
            isBulkExclusiveMode: false,
        }));
        this.props.setSelectedFreelancers([]);
    };

    onBulkAction = (key: string, val: { value: string }) => {
        if (val.value === 'send_sms') {
            this.setState({
                showSMS: true,
            });
        }
        if (val.value === 'send_notification') {
            this.setState({
                showNotification: true,
            });
        }
        if (val.value === 'add_workers_to_list') {
            this.setState({
                showAddToList: true,
            });
        }
        if (val.value === 'book_workers') {
            this.props.createMassBooking();
            this.setState({
                isBookingFreelancersMode: true,
                isSeriesBookingFreelancersMode: false,
                isInviteFreelancersMode: false,
            });
        }
        if (val.value === 'invite_workers') {
            this.props.createMassBooking();
            this.setState({
                isBookingFreelancersMode: false,
                isSeriesBookingFreelancersMode: false,
                isInviteFreelancersMode: true,
            });
        }
        if (val.value === 'book_workers_to_series') {
            this.setState({
                isBookingFreelancersMode: false,
                isSeriesBookingFreelancersMode: true,
                isInviteFreelancersMode: false,
            });
        }
    };

    selectAllRows = () => {
        let allSelectedFreelancers;
        const allFreelancers = this.props.data;
        allSelectedFreelancers = new Set([
            ...this.props.selectedFreelancers,
            ...allFreelancers
        ]);
        allSelectedFreelancers = Array.from(allSelectedFreelancers);
        this.props.setSelectedFreelancers(allSelectedFreelancers);
    }

    deselectAllRows = () => {
        this.props.setSelectedFreelancers([]);
    }

    isRowChecked = (rowInfo: TTableRow) => {
        const { isBulkExclusiveMode } = this.state;
        const isInSelectedFreelancers = this.props.selectedFreelancers.some(
            item => item.id === rowInfo.original.id,
        );
        return isBulkExclusiveMode
            ? !isInSelectedFreelancers
            : isInSelectedFreelancers;
    };

    onActionSuccess = () => {
        this.setState({
            isBulkMode: false,
            isBulkExclusiveMode: false,
        });
        this.props.setSelectedFreelancers([]);
    };

    onExpandRow = (rowInfo: TTableRow) => {
        const { id } = rowInfo.original.booking;
        history.push({
            pathname: `/operations/bookings/expanded/${id}/`,
            search: this.props.location.search,
        });
    };

    onCollapseRow = () => {
        const { id } = this.props.match.params;
        const pathname = this.props.location.pathname.replace(
            `/expanded/${id}`,
            '',
        );
        history.push({
            pathname,
            search: this.props.location.search,
        });
    };

    handleCancel = () => {
        axiosAbortController.abort();
        this.setState({
            isBookingFreelancersMode: false,
            isSeriesBookingFreelancersMode: false,
            isInviteFreelancersMode: false,
        });
        this.props.createMassBooking();
    };

    handleContinue = () => {
        axiosAbortController = new AbortController();

        const selectedFreelancersId = this.props.selectedFreelancers.map(
            freelancer => freelancer.id,
        );
        let massBookingPayload: TmassBookingPayload = {
            freelancers: selectedFreelancersId,
        };

        if (this.state.isSeriesBookingFreelancersMode) {
            const selectedSeriesId = this.props.selectedSeries.id;
            massBookingPayload = {
                ...massBookingPayload,
                series: selectedSeriesId,
            };
        } else {
            const selectedJobsId = this.props.selectedJobs.map(job => job.id);
            massBookingPayload = {
                ...massBookingPayload,
                jobrequests: selectedJobsId,
            };
        }

        this.props.createMassBookingAsync(
            massBookingPayload,
            this.state.isInviteFreelancersMode,
            axiosAbortController,
        );
    };

    expiringDVLACheckWarning = () => {
        const freelancerIds = this.props.massBookingResults
            .filter(result => result.freelancer.dvla_check_expires_soon)
            .map(result => result.freelancer.id);
        const uniqueFreelancerIds = [...new Set(freelancerIds)];

        return !!uniqueFreelancerIds.length && (
            <div className="mass-booking-dvla-warning">
                Warning: These workers&apos; DVLA checks expire in the next 14 days:<br/>
                {uniqueFreelancerIds.join(', ')}<br/>
                Please run a new check on their worker records immediately.
            </div>
        );
    }

    removeUnnecessaryStrings = (text: string) =>
        text.replace('{\'__all__\': [\'', '').replace('\']}', '');

    formatResults = (result: TMassBookingResult) => {
        const results = [];

        results.push(
            <span
                className={classNames(
                    'mass-booking-item-result',
                    massBookingResponseStatuses[
                        result.status.toUpperCase() as keyof typeof massBookingResponseStatuses
                    ],
                )}
                key={result.freelancer.id + result.jobrequest.id}
            >
                Worker {result.freelancer.id} -&gt; Job {result.jobrequest.id}{' '}
                {this.removeUnnecessaryStrings(result.message)}
                <br />
            </span>,
        );

        return results;
    };

    resultsOfMassJobBooking = () => (
        <div className="mass-booking-results-container">
            {this.props.massBookingResults.map(this.formatResults)}
        </div>
    );

    onAddToListSuccess = (id: string) => {
        this.onActionSuccess();
        history.push(`/availability-lists/?is_archived=false&availability_lists=${id}`);
    }

    render() {
        const massBookingModeName =
            history.location.search.includes(MASS_BOOKING_MODE)
                ? 'Mass booking'
                : history.location.search.includes(SERIES_BOOKING_MODE)
                    ? 'Series mass booking'
                    : null;

        const massBookingTitle = `${
            this.state.isBookingFreelancersMode || this.state.isSeriesBookingFreelancersMode
                ? 'Booking'
                : 'Invite'
        } workers`;
        const isActionStatusModalVisible =
            (this.state.isBookingFreelancersMode ||
                this.state.isSeriesBookingFreelancersMode ||
                this.state.isInviteFreelancersMode) &&
            this.props.massBookingResults.length > 0;

        const currentUrlParams = new URLSearchParams(history.location.search);

        return (
            <div className="table-container">
                {massBookingModeName && (
                    <div className="mass-booking-indicator">
                        {massBookingModeName} mode <strong>is active</strong>
                    </div>
                )}
                <div className="table-filter-container">
                    <SelectRole
                        onFilterChange={this.props.onFilterChange}
                        currentUrlParams={currentUrlParams}
                    />
                    {this.props.user.agency &&
                    (this.props.user.agency.features.includes('interviews') ||
                        this.props.user.agency.features.includes(
                            'presentation',
                        )) ? (
                        <InterviewDropDown
                            onFilterChange={this.props.onFilterChange}
                        />
                    ) : null}
                    <PublishedDropDown
                        onFilterChange={this.props.onFilterChange}
                    />
                    {!massBookingModeName &&
                        <BooleanDropdown
                            onFilterChange={this.props.onFilterChange}
                            currentUrlParams={currentUrlParams}
                            filterKey="is_archived"
                            falseLabel="Live"
                            trueLabel="Archived"
                        />
                    }
                    <SearchBar
                        filterKey="search"
                        onFilterChange={this.props.onFilterChange}
                        currentUrlParams={currentUrlParams}
                    />
                    <div className="table-action-bar">
                        <BulkActions
                            user={this.props.user}
                            items={this.props.selectedFreelancers}
                            onBulkAction={this.onBulkAction}
                            totalNumberOfItemsInTable={
                                this.props.meta && this.props.meta.count
                            }
                            isBulkExclusiveMode={this.state.isBulkExclusiveMode}
                            isBulkMode={this.state.isBulkMode}
                            selectedJobs={this.props.selectedJobs}
                            selectedSeries={this.props.selectedSeries}
                            isFreelancerRoute={true}
                            isMassBookingMode={history.location.search.includes(MASS_BOOKING_MODE)}
                            isSeriesMassBookingMode={history.location.search.includes(SERIES_BOOKING_MODE)}
                            selectAllRows={this.selectAllRows}
                            deselectAllRows={this.deselectAllRows}
                        />
                        <RefreshButton reloadData={this.props.reloadData} />
                    </div>
                </div>
                <MassBookingConfirmationModal
                    handleCancel={this.handleCancel}
                    handleContinue={this.handleContinue}
                    isBookingFreelancersMode={this.state.isBookingFreelancersMode}
                    isSeriesBookingFreelancersMode={this.state.isSeriesBookingFreelancersMode}
                    isInviteFreelancersMode={this.state.isInviteFreelancersMode}
                    isMassBookingCreating={this.props.isMassBookingCreating}
                    massBookingResultsError={this.props.massBookingResultsError}
                    massBookingTitle={massBookingTitle}
                    selectedFreelancers={this.props.selectedFreelancers}
                    selectedJobs={this.props.selectedJobs}
                    selectedSeries={this.props.selectedSeries}
                    notification={this.props.notification}
                />
                <Modal
                    className="modal"
                    contentLabel="Action status"
                    onRequestClose={this.handleCancel}
                    ariaHideApp={false}
                    isOpen={isActionStatusModalVisible}
                >
                    <p className="modal-title">Action status</p>
                    {this.expiringDVLACheckWarning()}
                    {this.resultsOfMassJobBooking()}
                    <button
                        className="continue btn--round-big"
                        onClick={this.handleCancel}
                        type="button"
                    >
                        OK
                    </button>
                </Modal>
                <Modal
                    className="none"
                    contentLabel="SMS Modal"
                    isOpen={this.state.showSMS}
                >
                    <SendSMSContainer
                        freelancers={this.props.selectedFreelancers}
                        exclusive={this.state.isBulkExclusiveMode}
                        totalNumberOfItemsInTable={
                            this.props.meta && this.props.meta.count
                        }
                        notification={this.props.notification}
                        onActionSuccess={this.onActionSuccess}
                        closeModal={() => this.setState({ showSMS: false })}
                    />
                </Modal>
                <Modal
                    className="none"
                    contentLabel="Notification Modal"
                    isOpen={this.state.showNotification}
                >
                    <SendNotificationContainer
                        freelancers={this.props.selectedFreelancers}
                        notification={this.props.notification}
                        onActionSuccess={this.onActionSuccess}
                        closeModal={() => this.setState({ showNotification: false })}
                    />
                </Modal>
                {this.state.showAddToList && (
                    <AddToAvailabilityListModal
                        freelancers={this.props.selectedFreelancers}
                        isOpen={this.state.showAddToList}
                        handleClose={() => this.setState({ showAddToList: false })}
                        notification={this.props.notification}
                        onActionSuccess={this.onAddToListSuccess}
                    />
                )}
                <CollapsibleTable
                    page={this.props.currentPage}
                    pageSize={0}
                    data={this.props.data}
                    pages={this.props.pages}
                    columns={this.tableColumns}
                    className="table-content"
                    loading={this.props.loading}
                    showPaginationBottom={this.props.showPaginationBottom}
                    showPageSizeOptions={false}
                    manual={true}
                    // eslint-disable-next-line no-return-assign, no-sequences
                    ref={table => (
                        // @ts-ignore
                        // eslint-disable-next-line no-sequences
                        (this.table = table), this.props.setTable(table)
                    )}
                    onPageChange={this.props.onPageChange}
                    onSortedChange={this.props.onSortedChange}
                    onRowCheckboxChange={this.onRowCheckboxChange}
                    onToggleBulkMode={this.onToggleBulkMode}
                    isBulkMode={this.state.isBulkMode}
                    isRowChecked={this.isRowChecked}
                    expandUsingURLPrimaryKey={false}
                    SubComponent={(row: {
                        original: {
                            id: any;
                            reference_number?: string;
                            last_name?: string;
                            first_name?: string;
                            email?: string;
                            mobile?: string;
                            gender?: string;
                            birth_date?: string;
                            service_key?: string;
                            service_role?: string;
                            referral_channel?: any;
                            date_joined?: string;
                            published?: boolean;
                            date_published?: string;
                            unpublication_reason?: any;
                            type_of_job_wanted?: string;
                            current_occupation?: string;
                            national_insurance_no?: any;
                            paye_statement?: string;
                            sort_code?: string;
                            account_number?: string;
                            address1?: string;
                            address2?: string;
                            city?: string;
                            postcode?: string;
                            passed_presentation?: boolean;
                            photo_thumbnail_medium?: string;
                            photo_thumbnail_large?: string;
                            full_name?: string;
                            latitude?: number;
                            longitude?: number;
                            travel_distance?: number;
                            referral_code?: string;
                            experience?: number;
                            years_experience?: number;
                            average_score?: any;
                            last_applied?: string;
                            is_evicted?: boolean;
                            eviction_reason?: any;
                            uploaded_documents?: TUploadedDocument[];
                            has_valid_documents?: boolean;
                            strikes_count?: number;
                            signup_medium?: string;
                            last_seen?: string;
                            total_points?: number;
                            completed_jobs?: number;
                            managing_agency?: TManagingAgency;
                            connections_count?: any;
                            is_active?: boolean;
                            rich_description?: string;
                            roles?: TFreelancerRole[];
                        };
                    }) => (
                        <div className="expanded-content">
                            <ActionButton
                                text="View details"
                                onAction={() =>
                                    history.push(
                                        `/operations/freelancer/${row.original.id}/`,
                                    )
                                }
                            />
                            {(
                                this.props.user.agency.features.includes('dashboard_sms') &&
                                this.props.user.permissions.includes('auth.sms_dashboard_allowed')
                            ) && (
                                <>
                                    <ActionButton
                                        text="Send notification..."
                                        onAction={() => {
                                            this.setState({
                                                isBulkMode: false,
                                                isBulkExclusiveMode: false,
                                                showNotification: true,
                                            });

                                            this.props.setSelectedFreelancers([
                                                // @ts-ignore
                                                row.original,
                                            ]);
                                        }}
                                        requiredFeature="dashboard_sms"
                                        requiredPermission="auth.sms_dashboard_allowed"
                                    />
                                    <ActionButton
                                        text="Send SMS..."
                                        onAction={() => {
                                            this.setState({
                                                isBulkMode: false,
                                                isBulkExclusiveMode: false,
                                                showSMS: true,
                                            });

                                            this.props.setSelectedFreelancers([
                                                // @ts-ignore
                                                row.original,
                                            ]);
                                        }}
                                        requiredFeature="dashboard_sms"
                                        requiredPermission="auth.sms_dashboard_allowed"
                                    />
                                </>
                            )}
                        </div>
                    )}
                />
            </div>
        );
    }

    tableColumns = [
        {
            Header: 'Ref',
            accessor: 'reference_number',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TTableRow) => (
                <div>
                    <Link
                        className="group-link"
                        to={`/operations/freelancer/${row.original.id}`}
                    >
                        {row.value}
                    </Link>
                </div>
            ),
        },
        {
            Header: 'Photo',
            accessor: 'freelancer_detail.photo_thumbnail_medium',
            sortable: false,
            headerClassName: 'xsmall-col',
            className: 'xsmall-col',
            Cell: (row: TTableRow) => {
                const freelancerImg = row.original.photo_thumbnail_large
                    ? row.original.photo_thumbnail_large
                    : '/static/images/default_profile.png';
                return (
                    <div>
                        <a
                            className="group-link"
                            href={freelancerImg}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <div
                                className="avatar"
                                style={{
                                    backgroundImage: `url(${freelancerImg})`,
                                }}
                            />
                        </a>
                    </div>
                );
            },
        },

        {
            Header: 'Name',
            accessor: 'full_name',
            Cell: (row: TTableRow) => (
                <span title={row.value}>{row.value} </span>
            ),
        },
        {
            Header: 'Service',
            accessor: 'service_key',
            headerClassName: 'small-col',
            className: 'small-col upper-case',
        },
        {
            Header: 'Role(s)',
            accessor: 'service_role',
            headerClassName: 'small-col',
            className: 'small-col upper-case',
            Cell: (row: TTableRow) => (
                <span title={row.value}>{row.value} </span>
            ),
        },
        {
            Header: 'Skills',
            accessor: 'rich_description',
            headerClassName: 'small-col',
            className: 'small-col upper-case',
            Cell: (row: TTableRow) => (
                <span title={row.value}>{row.value} </span>
            ),
        },
        {
            Header: 'Status',
            sortable: false,
            headerClassName: 'xsmall-col',
            className: 'xsmall-col upper-case',
            Cell: (row: TTableRow) =>
                !row.original.is_active ? (
                    <Tooltip tooltip="This worker is disabled">
                        <i className="icon icon-medium with-margin icon-help-circle grey" />
                    </Tooltip>
                ) : row.original.has_upcoming_booking ? (
                    <Tooltip tooltip="This worker is published and in an upcoming booking">
                        <i className="icon icon-medium with-margin icon-check-circle-22 blue" />
                    </Tooltip>
                ) : row.original.published ? (
                    <Tooltip tooltip="This worker is published and receives offers">
                        <i className="icon icon-medium with-margin icon-check-circle-22 green" />
                    </Tooltip>
                ) : (
                    <Tooltip
                        tooltip={`This worker is unpublished because of: ${row.original.unpublication_reason}`}
                    >
                        <i className="icon icon-medium with-margin icon-remove-circle-11 red" />
                    </Tooltip>
                ),
        },
        {
            Header: 'Mobile',
            accessor: 'mobile',
            headerClassName: 'small-col',
            className: 'small-col',
            sortable: false,
            show: this.props.user.isAgent,
            Cell: (row: TTableRow) => {
                const mobileLink = `tel:${row.value}`;
                return (
                    <a
                        className="group-link"
                        href={mobileLink}
                        title={row.value}
                    >
                        {row.value && row.value.length === 11
                            ? `${row.value.substr(0, 5)} ${row.value.substr(
                                  5,
                                  11,
                              )}`
                            : row.value}
                    </a>
                );
            },
        },
        {
            Header: 'Email',
            accessor: 'email',
            sortable: false,
            show: this.props.user.isAgent,
            Cell: (row: TTableRow) => {
                const mailToAddress = `mailto:${row.value}`;
                return (
                    <a
                        className="group-link"
                        href={mailToAddress}
                        title={row.value}
                    >
                        {row.value}
                    </a>
                );
            },
        },
        {
            Header: 'Rating',
            accessor: 'average_score',
            headerClassName: 'xsmall-col col-center',
            className: 'xsmall-col col-center',
        },
    ];
}
